import globalMethods from "@/globalMethods";
import {URLS, GOOD_FORMATS} from "@/constants";
import {notify} from "@/plugins/notification";
import i18n from "@/i18n";


function buildAuthHeaders(rootState, {withTimezone = false, withShopId = false, withCsrf = false} = {}) {
    const {personalData, company, cashRegister} = rootState;
    const headers = {
        role: personalData.role,
        companyId: company.companyData?.token,
    };
    if (withTimezone) headers.timezone = cashRegister.cashRegisterData?.timezone;
    if (withShopId) headers.shopId = globalMethods.getDataFromLocalStorage("currentCashRegisterId");
    if (withCsrf) {
        headers["Content-Type"] = "application/json";
        headers["X-CSRFToken"] = globalMethods.getCookie("csrftoken");
    }
    return headers;
}

function appendGoodListFilters(params, state, goodCategory) {
    if (state.goodsPagination.search) params.set("search", state.goodsPagination.search);
    if (goodCategory.selectedCategoryId !== null) {
        params.set("category_id", goodCategory.selectedCategoryId);
        if (goodCategory.selectedCategoryId !== "none") {
            params.set("include_subcategories", "true");
        }
    }
    if (goodCategory.selectedKindFilter) params.set("good_format", goodCategory.selectedKindFilter);
    if (state.filterWarehouseId && state.filterWarehouseId !== "all" && state.filterWarehouseId !== "main") {
        params.set("shop-id", state.filterWarehouseId);
    }
    if (!state.filterShowHidden) params.set("hide-archived-goods", "true");
}


export default {
    namespaced: true,
    state: {
        goods: [],
        cashRegisterGoods: [],
        goodsPagination: {
            page: 1,
            pageSize: 50,
            hasNext: false,
            totalCount: 0,
            sortBy: null,
            sortDirection: "asc",
            search: "",
            isLoading: false,
        },
        filterWarehouseId: "all",
        filterShowHidden: false,
    },
    mutations: {
        addCashRegisterToGoods(state, params) {
            const {newCashRegister, goodIds} = params;
            for (const goodId of goodIds) {
                const good = state.goods.find(good => good.id === goodId);
                good.shop_ids.push(newCashRegister.id);
            }
        },
        setCashRegisterGoods(state, data) {
            state.cashRegisterGoods = data;
        },
        addGood(state, goodData) {
            state.goods.unshift(goodData);
            state.goodsPagination.totalCount++;
            if (goodData.is_active && !goodData.is_deleted) {
                state.cashRegisterGoods.unshift(goodData);
            }
        },
        updateGood(state, newGoodData) {
            const good = state.goods.find(good => good.id === newGoodData.id);
            if (!good) return;
            good.name = newGoodData.name;
            good.price = newGoodData.price;
            good.format = newGoodData.format;
            good.type = newGoodData.type;
            good.verbose_id = newGoodData.verbose_id;
            good.shop_ids = newGoodData.shop_ids;
            good.characteristics = newGoodData.characteristics;
            good.is_active = newGoodData.is_active;
            good.amount = newGoodData.amount;
            good.category_id = newGoodData.category_id;
            good.category = newGoodData.category;
        },
        activateGood(state, goodId) {
            const good = state.goods.find(good => good.id === goodId);
            if (good) good.is_active = true;
        },
        activateGoods(state, goodIds) {
            for (const goodId of goodIds) {
                const good = state.goods.find(good => good.id === goodId);
                if (good) good.is_active = true;
            }
        },
        archiveGood(state, goodId) {
            const good = state.goods.find(good => good.id === goodId);
            if (good) good.is_active = false;
        },
        archiveGoods(state, goodIds) {
            for (const goodId of goodIds) {
                const good = state.goods.find(good => good.id === goodId);
                if (good) good.is_active = false;
            }
        },
        deleteGood(state, goodId) {
            state.goods = state.goods.filter(good => good.id !== goodId);
            state.goodsPagination.totalCount = Math.max(0, state.goodsPagination.totalCount - 1);
        },
        deleteGoods(state, goodIds) {
            for (const goodId of goodIds) {
                state.goods = state.goods.filter(good => good.id !== goodId);
            }
            state.goodsPagination.totalCount = Math.max(0, state.goodsPagination.totalCount - goodIds.length);
        },
        setGoods(state, data) {
            state.goods = data;
        },
        setGoodsPaginated(state, { data, hasNext, totalCount, append }) {
            if (append) {
                state.goods = [...state.goods, ...data];
            } else {
                state.goods = data;
            }
            state.goodsPagination.hasNext = hasNext;
            state.goodsPagination.totalCount = totalCount;
        },
        setGoodsPaginationLoading(state, isLoading) {
            state.goodsPagination.isLoading = isLoading;
        },
        setGoodsPaginationParams(state, params) {
            if (params.page !== undefined) state.goodsPagination.page = params.page;
            if (params.sortBy !== undefined) state.goodsPagination.sortBy = params.sortBy;
            if (params.sortDirection !== undefined) state.goodsPagination.sortDirection = params.sortDirection;
            if (params.search !== undefined) state.goodsPagination.search = params.search;
        },
        resetGoodsPagination(state) {
            state.goodsPagination.page = 1;
            state.goodsPagination.hasNext = false;
            state.goodsPagination.totalCount = 0;
        },
        decreaseCashRegisterGoodsAmount(state, data) {
            const {newTransactionData, cashRegisterPk} = data;
            for (const operation of newTransactionData.operations) {
                if (operation.good.format === GOOD_FORMATS.GOOD) {
                    const cashRegisterGood = state.cashRegisterGoods.find(good => good.id === operation.good.id)
                    cashRegisterGood.amount[cashRegisterPk] -= operation.amount;

                    const good = state.goods.find(good => good.id === operation.good.id);
                    if (good) good.amount[cashRegisterPk] -= operation.amount;
                }
            }
        },
        increaseGoodsAmount(state, supplyData) {
            // Update both `goods` (paginated catalog list) and `cashRegisterGoods` (full list used
            // by Sales / cash register). Without touching cashRegisterGoods the sales screen would
            // show stale 0 amounts until a full reload.
            for (const position of supplyData.positions) {
                const targets = [
                    state.goods.find(good => good.id === position.good.id),
                    state.cashRegisterGoods.find(good => good.id === position.good.id),
                ];
                for (const good of targets) {
                    if (!good) continue;
                    good.amount[supplyData.warehouse] = good.amount[supplyData.warehouse] || 0;
                    good.amount[supplyData.warehouse] += position.amount;
                }
            }
        },
        updateGoodsAmount(state, warehouseMoveData) {
            // Mirror amount changes to both `goods` and `cashRegisterGoods` (see increaseGoodsAmount).
            const isWriteOff = warehouseMoveData.is_write_off;
            for (const position of warehouseMoveData.positions) {
                const targets = [
                    state.goods.find(good => good.id === position.good.id),
                    state.cashRegisterGoods.find(good => good.id === position.good.id),
                ];
                for (const good of targets) {
                    if (!good) continue;
                    good.amount[warehouseMoveData.warehouse_from] = good.amount[warehouseMoveData.warehouse_from] || 0;
                    good.amount[warehouseMoveData.warehouse_from] -= position.amount;
                    if (!isWriteOff) {
                        good.amount[warehouseMoveData.warehouse_to] = good.amount[warehouseMoveData.warehouse_to] || 0;
                        good.amount[warehouseMoveData.warehouse_to] += position.amount;
                    }
                }
            }
        },
        setFilterWarehouseId(state, id) {
            state.filterWarehouseId = id;
        },
        setFilterShowHidden(state, value) {
            state.filterShowHidden = value;
        },
    },
    actions: {
        async getAllGoodsForSearch({commit, rootState}) {
            const url = `${URLS.MODEL.GOOD.GOODS_URL}?hide-archived-goods=true&sort=alphabet`;
            const options = {
                method: "GET",
                headers: buildAuthHeaders(rootState),
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                commit("setCashRegisterGoods", data.data);
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },
        async getCashRegisterGoods({commit, rootState}) {
            const currentCashRegisterId = globalMethods.getDataFromLocalStorage("currentCashRegisterId");
            const url = `${URLS.MODEL.GOOD.GOODS_URL}?hide-archived-goods=true&shop-id=${currentCashRegisterId}&sort=alphabet`;
            const options = {
                method: "GET",
                headers: buildAuthHeaders(rootState, {withTimezone: true, withShopId: true}),
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                commit("setCashRegisterGoods", data.data);
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },
        async createGood({commit, dispatch, rootState}, params) {
            const {format, type, name, verbose_id, price, characteristics, shops_data, category} = params;
            const options = {
                method: "POST",
                headers: buildAuthHeaders(rootState, {withTimezone: true, withCsrf: true}),
                body: JSON.stringify({
                    format, type, name, verbose_id, price, characteristics, shops_data,
                    category: category || null,
                    amount: {},
                }),
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                commit("addGood", data.data);
                dispatch("goodCategory/loadAllCategories", null, {root: true});
                notify(i18n.global.t("Item successfully added to the catalog"));
                return {successfully: true, goodData: data.data};
            };
            return globalMethods.sendRequestToGetJson({url: URLS.MODEL.GOOD.GOODS_URL, options, successfulHandler});
        },
        async updateGood({commit, dispatch, rootState}, params) {
            const {goodId, name, price, format, type, verbose_id, shops_data, characteristics, is_active, delete_unlinked_warehouse_goods, amount, category} = params;
            const url = URLS.MODEL.GOOD.GOOD_URL.replace("<ID>", goodId);
            const options = {
                method: "PUT",
                headers: buildAuthHeaders(rootState, {withTimezone: true, withCsrf: true}),
                body: JSON.stringify({
                    name, price, format, type, verbose_id, shops_data, characteristics,
                    is_active, delete_unlinked_warehouse_goods, amount,
                    category: category !== undefined ? (category || null) : undefined,
                }),
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                commit("updateGood", data.data);
                dispatch("goodCategory/loadAllCategories", null, {root: true});
                notify(i18n.global.t("Item successfully updated"));
                return {successfully: true, detail: null};
            };
            const errorHandler = (data) => ({successfully: false, detail: data});
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler, errorHandler});
        },
        async showGood({commit, rootState}, goodId) {
            const url = URLS.MODEL.GOOD.GOOD_URL.replace("<ID>", goodId);
            const options = {
                method: "PATCH",
                headers: buildAuthHeaders(rootState, {withTimezone: true, withCsrf: true}),
                body: JSON.stringify({command: "activate"}),
                credentials: 'include',
            };
            const successfulHandler = () => {
                commit("activateGood", goodId);
                notify(i18n.global.t("Item successfully removed from hidden"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },
        async showGoods({commit, rootState}, goodIds) {
            const options = {
                method: "PATCH",
                headers: buildAuthHeaders(rootState, {withTimezone: true, withCsrf: true}),
                body: JSON.stringify({command: "activate", good_ids: goodIds}),
                credentials: 'include',
            };
            const successfulHandler = () => {
                commit("activateGoods", goodIds);
                notify(i18n.global.t("Selected items successfully removed from hidden"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: URLS.MODEL.GOOD.GOODS_URL, options, successfulHandler});
        },
        async hideGood({commit, rootState}, goodId) {
            const url = URLS.MODEL.GOOD.GOOD_URL.replace("<ID>", goodId);
            const options = {
                method: "PATCH",
                headers: buildAuthHeaders(rootState, {withTimezone: true, withCsrf: true}),
                body: JSON.stringify({command: "archive"}),
                credentials: 'include',
            };
            const successfulHandler = () => {
                commit("archiveGood", goodId);
                notify(i18n.global.t("Item successfully added to hidden"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },
        async hideGoods({commit, rootState}, goodIds) {
            const options = {
                method: "PATCH",
                headers: buildAuthHeaders(rootState, {withTimezone: true, withCsrf: true}),
                body: JSON.stringify({command: "archive", good_ids: goodIds}),
                credentials: 'include',
            };
            const successfulHandler = () => {
                commit("archiveGoods", goodIds);
                notify(i18n.global.t("Selected items successfully added to hidden"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: URLS.MODEL.GOOD.GOODS_URL, options, successfulHandler});
        },
        async deleteGood({commit, dispatch, rootState}, goodId) {
            const url = URLS.MODEL.GOOD.GOOD_URL.replace("<ID>", goodId);
            const options = {
                method: "DELETE",
                headers: buildAuthHeaders(rootState, {withTimezone: true, withCsrf: true}),
                credentials: 'include',
            };
            const successfulHandler = () => {
                commit("deleteGood", goodId);
                dispatch("goodCategory/loadAllCategories", null, {root: true});
                notify(i18n.global.t("Item successfully removed"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },
        async deleteGoods({commit, dispatch, rootState}, goodIds) {
            const options = {
                method: "DELETE",
                headers: buildAuthHeaders(rootState, {withTimezone: true, withCsrf: true}),
                body: JSON.stringify({good_ids: goodIds}),
                credentials: 'include',
            };
            const successfulHandler = () => {
                commit("deleteGoods", goodIds);
                dispatch("goodCategory/loadAllCategories", null, {root: true});
                notify(i18n.global.t("Selected items have been successfully removed"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: URLS.MODEL.GOOD.GOODS_URL, options, successfulHandler});
        },
        async linkGoodsToShop({rootState}, data) {
            const {cashRegisterId, goodIds} = data;
            const options = {
                method: "POST",
                headers: buildAuthHeaders(rootState, {withCsrf: true}),
                credentials: "include",
                body: JSON.stringify({shop_id: cashRegisterId, good_ids: goodIds}),
            };
            const successfulHandler = () => {
                notify(i18n.global.t("Catalog items have been successfully linked to the cash register"), "black");
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: URLS.MODEL.GOOD.GOODS_LINK_URL, options, successfulHandler});
        },
        async unlinkGoodsToShop({rootState}, data) {
            const {cashRegisterId, goodIds, mode} = data;
            const options = {
                method: "DELETE",
                headers: buildAuthHeaders(rootState, {withCsrf: true}),
                credentials: "include",
                body: JSON.stringify({shop_id: cashRegisterId, good_ids: goodIds, mode}),
            };
            const successfulHandler = () => {
                notify(i18n.global.t("Catalog items have been successfully unlinked from the cash register"), "black");
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: URLS.MODEL.GOOD.GOODS_LINK_URL, options, successfulHandler});
        },
        async getGoods({commit, state, rootState}, { append = false } = {}) {
            const pagination = state.goodsPagination;
            const params = new URLSearchParams();
            if (pagination.page >= 1) {
                params.set("page", pagination.page);
                params.set("page_size", pagination.pageSize);
            }
            if (pagination.sortBy) {
                params.set("sort_by", pagination.sortBy);
                params.set("sort_direction", pagination.sortDirection);
            }
            appendGoodListFilters(params, state, rootState.goodCategory);
            const queryString = params.toString();
            const url = queryString ? `${URLS.MODEL.GOOD.GOODS_URL}?${queryString}` : URLS.MODEL.GOOD.GOODS_URL;
            const options = {
                method: "GET",
                headers: buildAuthHeaders(rootState),
                credentials: 'include',
            };
            commit("setGoodsPaginationLoading", true);
            const successfulHandler = (data) => {
                commit("setGoodsPaginated", {
                    data: data.data,
                    hasNext: data.has_next ?? false,
                    totalCount: data.total_count ?? data.data.length,
                    append,
                });
                commit("setGoodsPaginationLoading", false);
                return {successfully: true};
            };
            const errorHandler = (data) => {
                commit("setGoodsPaginationLoading", false);
                return {successfully: false, detail: data};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler, errorHandler});
        },
        async loadNextGoodsPage({commit, state, dispatch}) {
            if (!state.goodsPagination.hasNext || state.goodsPagination.isLoading) return;
            commit("setGoodsPaginationParams", { page: state.goodsPagination.page + 1 });
            return dispatch("getGoods", { append: true });
        },
        async changeGoodsSort({commit, dispatch}, { sortBy, sortDirection }) {
            commit("resetGoodsPagination");
            commit("setGoodsPaginationParams", { page: 1, sortBy, sortDirection });
            return dispatch("getGoods");
        },
        async searchGoods({commit, dispatch}, search) {
            commit("resetGoodsPagination");
            commit("setGoodsPaginationParams", { page: 1, search });
            return dispatch("getGoods");
        },
        async fetchAllMatchingGoodIds({state, rootState}) {
            const params = new URLSearchParams();
            params.set("only_ids", "true");
            appendGoodListFilters(params, state, rootState.goodCategory);
            const url = `${URLS.MODEL.GOOD.GOODS_URL}?${params.toString()}`;
            const options = {
                method: "GET",
                headers: buildAuthHeaders(rootState),
                credentials: "include",
            };
            const successfulHandler = (data) => ({successfully: true, ids: data.data || []});
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },
        async assignCategoryToGoods({rootState, dispatch}, {goodIds, categoryId}) {
            const options = {
                method: "PATCH",
                headers: buildAuthHeaders(rootState, {withCsrf: true}),
                body: JSON.stringify({
                    command: "assign_category",
                    good_ids: goodIds,
                    category_id: categoryId,
                }),
                credentials: "include",
            };
            const successfulHandler = () => {
                dispatch("getGoods");
                dispatch("goodCategory/loadAllCategories", null, {root: true});
                notify(i18n.global.t("Category assigned successfully"), "black");
                return {successfully: true};
            };
            const errorHandler = (data) => {
                const message = typeof data?.detail === "string" ? data.detail : i18n.global.t("Failed to assign category");
                notify(message, "red", 5000);
                return {successfully: false, detail: data?.detail};
            };
            return globalMethods.sendRequestToGetJson({url: URLS.MODEL.GOOD.GOODS_URL, options, successfulHandler, errorHandler});
        },
    }
};
